import { useMemo } from 'react';

import type {
  SelectionList,
  SelectionListIdNames,
  ValidValueTypes,
} from '@revelio/iso-utility';

import { useAllCustomSkillLevels } from '../../../../../../shared/selection-lists/hooks/useAllCustomSkillLevels';
import { useSpecificCustomSkills } from '../../../../../../shared/selection-lists/hooks/useSpecificCustomSkills';
import {
  filterToSupportedLevels,
  skillLevelToListName,
} from './custom-skills.helpers';

/**
 * Builds selection lists for Custom Skill taxonomy levels so BreadcrumbTree can render a tree.
 */
export const useReportsCustomSkills = (taxonomyId: number) => {
  const { levels } = useAllCustomSkillLevels();

  const { data } = useSpecificCustomSkills({
    levelIds: filterToSupportedLevels(levels).map((l) => l.level_id),
    taxonomyId: taxonomyId,
  });

  const lists = useMemo<SelectionList<ValidValueTypes>[]>(() => {
    if (!data || data.length === 0) return [];

    return data.map((levelData, index) => {
      const listId = skillLevelToListName(levelData);
      const previousLevelData = data[index - 1];
      const parentListIdRaw = previousLevelData
        ? skillLevelToListName(previousLevelData)
        : undefined;

      const label = levelData.label.label;

      const value = levelData.refs.map((ref) => {
        const parents = Array.isArray(ref.parents) ? ref.parents : [];
        const parentIndex = index - 1;
        const parentIdValue =
          parentIndex >= 0 ? parents[parentIndex] : undefined;

        return {
          id: String(ref.id),
          shortName: ref.short_name,
          longName: ref.long_name,
          ...(parentListIdRaw && parentIdValue != null
            ? { [parentListIdRaw]: String(parentIdValue) }
            : {}),
          ...(Array.isArray(ref.titles) && ref.titles.length
            ? { rawTitles: ref.titles }
            : {}),
        };
      });

      return {
        id: listId,
        label: label as unknown as SelectionListIdNames,
        value,
      } as SelectionList<ValidValueTypes>;
    });
  }, [data]);

  return { lists };
};
